import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { PaginationData } from '../../shared/components/pagination-bar/pagination-bar.component';
import { Company, CompanyService } from '../services/company.service';
import { CompanyListComponent } from '../components/company-list/company-list.component';
import { CompanyAdminImpersonationService } from '../impersonation/services/company-admin-impersonation.service';
import { ImpersonationConfirmModalComponent } from '../../shared/components/impersonation-confirm-modal';

@Component({
  selector: 'app-admin-companies',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TitleBarComponent,
    CompanyListComponent,
    ImpersonationConfirmModalComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './admin-companies.component.html',
})
export class AdminCompaniesComponent implements OnInit, OnDestroy {
  private readonly companyService = inject(CompanyService);
  private readonly router = inject(Router);
  private readonly impersonationLauncher = inject(CompanyAdminImpersonationService);

  companies: Company[] = [];
  searchTerm = '';
  loading = true;
  errorMessage = '';

  /** Company awaiting impersonation confirmation, chosen from a row menu. */
  impersonateTarget: Company | null = null;

  currentPage = 1;
  itemsPerPage = 20;
  totalItems = 0;
  totalPages = 0;

  private readonly search$ = new Subject<string>();
  private searchSub?: Subscription;
  private companiesRequestSub?: Subscription;

  ngOnInit(): void {
    this.loadCompanies();
    this.searchSub = this.search$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.loadCompanies();
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.companiesRequestSub?.unsubscribe();
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
    this.search$.next(value.trim());
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadCompanies();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.loadCompanies();
  }

  onOnboardCompany(): void {
    void this.router.navigate(['/admin/companies/new']);
  }

  get impersonateBusy(): boolean {
    return this.impersonationLauncher.busyCompanyId === this.impersonateTarget?.id;
  }

  canImpersonate(company: Company): boolean {
    return this.impersonationLauncher.canImpersonate(company);
  }

  adminLabel(company: Company): string {
    return this.impersonationLauncher.adminLabel(company);
  }

  requestImpersonate(company: Company): void {
    if (!this.canImpersonate(company)) {
      this.impersonationLauncher.warnMissingAdminId();
      return;
    }
    this.impersonateTarget = company;
  }

  cancelImpersonate(): void {
    if (this.impersonateBusy) {
      return;
    }
    this.impersonateTarget = null;
  }

  confirmImpersonate(): void {
    const company = this.impersonateTarget;
    if (!company || this.impersonateBusy) {
      return;
    }
    this.impersonationLauncher.start(company, () => {
      this.impersonateTarget = null;
    });
  }

  loadCompanies(): void {
    this.companiesRequestSub?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';

    const params: { page: number; limit: number; search?: string } = {
      page: this.currentPage,
      limit: this.itemsPerPage,
    };
    if (this.searchTerm.trim()) {
      params.search = this.searchTerm.trim();
    }

    this.companiesRequestSub = this.companyService.listCompaniesPaginated(params).subscribe({
      next: res => {
        this.loading = false;
        if (res.status && res.data) {
          this.companies = res.data.companies || [];
          this.totalItems = res.data.totalItems || 0;
          this.currentPage = res.data.currentPage || this.currentPage;
          this.totalPages = res.data.totalPages || 0;
          this.itemsPerPage = res.data.itemsPerPage || this.itemsPerPage;
          return;
        }
        this.companies = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.errorMessage = res.message || 'Unable to load companies.';
      },
      error: err => {
        this.loading = false;
        this.companies = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load companies.';
      },
    });
  }
}
